import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FiCopy, FiLoader, FiLogOut, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import {
  cDeleteGroup, cLeaveGroup, cResetGroupLink, cUpdateGroup, cUpload, fullUrl,
} from '../../api';
import { useLanguage } from '../../i18n';
import { BLUE, BLUE_GRADIENT } from './brand';

/**
 * Group settings (group **admins** only): the **nickname**, the **logo**, the
 * about text, **public ⇄ private**, “members may invite”, the invite link
 * (copy / regenerate), plus “leave the group” and “delete the group”.
 */
export default function GroupSettings({ group, onUpdated, onLeft, onDeleted }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: group.name || '',
    about: group.about || '',
    avatar: group.avatar || '',
    cover: group.cover || '',
    is_public: !!group.is_public,
    allow_member_invite: group.allow_member_invite !== false,
  });
  const [link, setLink] = useState(group.invite_link || '');
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);

  const input = 'w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm outline-none focus:border-gray-400';

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const res = await cUpload([file]);
      const url = res.urls?.[0] || '';
      setForm((f) => ({ ...f, avatar: url }));
      const saved = await cUpdateGroup(group.id, { avatar: url });
      if (onUpdated) onUpdated(saved.group);
      toast.success(t('saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setLogoBusy(false);
    }
  };

  /** រូបភាពគម្របក្រុម — the banner shown at the top of the group page. */
  const pickCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverBusy(true);
    try {
      const res = await cUpload([file]);
      const url = res.urls?.[0] || '';
      setForm((f) => ({ ...f, cover: url }));
      const saved = await cUpdateGroup(group.id, { cover: url });
      if (onUpdated) onUpdated(saved.group);
      toast.success(t('saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setCoverBusy(false);
    }
  };

  const removeCover = async () => {
    try {
      setForm((f) => ({ ...f, cover: '' }));
      const saved = await cUpdateGroup(group.id, { cover: '' });
      if (onUpdated) onUpdated(saved.group);
      toast.success(t('saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await cUpdateGroup(group.id, {
        name: form.name.trim(),
        about: form.about.trim(),
        is_public: form.is_public,
        allow_member_invite: form.allow_member_invite,
      });
      if (onUpdated) onUpdated(res.group);
      toast.success(t('updatedOk'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('linkCopied'));
    } catch (e) { toast(link); }
  };

  const resetLink = async () => {
    try {
      const res = await cResetGroupLink(group.id);
      setLink(res.invite_link);
      toast.success(t('groupResetLink'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const leave = async () => {
    try {
      await cLeaveGroup(group.id);
      toast.success(t('groupLeave'));
      if (onLeft) onLeft();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const remove = async () => {
    if (!window.confirm(t('groupDeleteConfirm'))) return;
    try {
      await cDeleteGroup(group.id);
      toast.success(t('deletedOk'));
      if (onDeleted) onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  return (
    <div className="space-y-3">
      <form onSubmit={save} className="space-y-2.5">
        <div className="flex items-center gap-3">
          {form.avatar ? (
            <img src={fullUrl(form.avatar)} alt={form.name} className="w-14 h-14 rounded-xl object-cover" />
          ) : (
            <span
              className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-extrabold"
              style={{ background: BLUE }}
            >
              {(form.name || 'G').slice(0, 1).toUpperCase()}
            </span>
          )}
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
            <input type="file" accept="image/*" hidden onChange={pickLogo} />
            {logoBusy && <FiLoader className="w-4 h-4 animate-spin" />}
            {t('groupUploadLogo')}
          </label>
        </div>

        {/* រូបភាពគម្របក្រុម — the banner of the group page */}
        <div>
          <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('groupCover')}</span>
          {form.cover ? (
            <img
              src={fullUrl(form.cover)}
              alt={t('groupCover')}
              className="w-full h-24 rounded-xl object-cover mb-1.5"
            />
          ) : (
            <span className="block w-full h-24 rounded-xl mb-1.5" style={{ background: BLUE_GRADIENT }} />
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
              <input type="file" accept="image/*" hidden onChange={pickCover} />
              {coverBusy && <FiLoader className="w-4 h-4 animate-spin" />}
              {t('groupUploadCover')}
            </label>
            {form.cover && (
              <button
                type="button"
                onClick={removeCover}
                className="text-xs font-bold text-red-500 hover:underline"
              >
                {t('remove')}
              </button>
            )}
            <span className="text-[11px] text-gray-400">{t('groupCoverHint')}</span>
          </div>
        </div>

        <label className="block">
          <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('groupName')}</span>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={input} />
        </label>
        <label className="block">
          <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('groupAbout')}</span>
          <textarea value={form.about} onChange={(e) => setForm((f) => ({ ...f, about: e.target.value }))} rows={2} className={input} />
        </label>

        <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-gray-600 dark:text-gray-300">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_public}
              onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))}
            />
            {form.is_public ? t('groupPublicHint') : t('groupPrivateHint')}
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.allow_member_invite}
              onChange={(e) => setForm((f) => ({ ...f, allow_member_invite: e.target.checked }))}
            />
            {t('groupAllowMemberInvite')}
          </label>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-bold disabled:opacity-60"
          style={{ background: BLUE }}
        >
          {busy && <FiLoader className="w-4 h-4 animate-spin" />} {t('save')}
        </button>
      </form>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
        <p className="text-[11px] font-bold text-gray-500 mb-1">{t('groupInviteLink')}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            readOnly
            value={link}
            className="flex-1 min-w-[140px] bg-transparent text-xs text-gray-600 dark:text-gray-300 truncate"
          />
          <button type="button" onClick={copyLink} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold text-white" style={{ background: BLUE }}>
            <FiCopy className="w-3.5 h-3.5" /> {t('groupCopyLink')}
          </button>
          <button type="button" onClick={resetLink} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-gray-100 dark:bg-gray-800">
            <FiRefreshCw className="w-3.5 h-3.5" /> {t('groupResetLink')}
          </button>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">{t('groupInviteLinkHint')}</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={leave}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-gray-100 dark:bg-gray-800"
        >
          <FiLogOut className="w-4 h-4" /> {t('groupLeave')}
        </button>
        <button
          type="button"
          onClick={remove}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-red-600 border border-red-200"
        >
          <FiTrash2 className="w-4 h-4" /> {t('delete')}
        </button>
      </div>
    </div>
  );
}
